import React, { Suspense, useState } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { UTMPersistence } from './components/routing/UTMPersistence';
import { AuthGate } from './components/routing/AuthGate';
import { GuestAllowed } from './components/routing/GuestAllowed';
import { PlanGate } from './components/routing/PlanGate';
import { ScrollToTop } from './components/routing/ScrollToTop';
import { JournalContextProvider } from './context/JournalContext';
import { AppReturnBanner } from './components/common/AppReturnBanner';

// Lazy load page components for code splitting
const LandingPage = React.lazy(() => import('./pages/LandingPage').then(module => ({ default: module.LandingPage })));
const Pricing = React.lazy(() => import('./pages/marketing/Pricing'));
const Mentoring = React.lazy(() => import('./pages/marketing/Mentoring').then(module => ({ default: module.Mentoring })));
const Webinar = React.lazy(() => import('./pages/marketing/Webinar').then(module => ({ default: module.Webinar })));
const FreeWorkbookLeadMagnet = React.lazy(() => import('./pages/marketing/FreeWorkbookLeadMagnet').then(module => ({ default: module.FreeWorkbookLeadMagnet })));
const Welcome = React.lazy(() => import('./pages/marketing/Welcome').then(module => ({ default: module.Welcome })));
const CheckoutSuccess = React.lazy(() => import('./pages/checkout/CheckoutSuccess').then(module => ({ default: module.CheckoutSuccess })));
const CheckoutCancel = React.lazy(() => import('./pages/checkout/CheckoutCancel').then(module => ({ default: module.CheckoutCancel })));
const Privacy = React.lazy(() => import('./pages/legal/Privacy').then(module => ({ default: module.Privacy })));
const Terms = React.lazy(() => import('./pages/legal/Terms').then(module => ({ default: module.Terms })));
const DataProcessing = React.lazy(() => import('./pages/legal/DataProcessing').then(module => ({ default: module.DataProcessing })));
const About = React.lazy(() => import('./pages/marketing/About').then(module => ({ default: module.About })));
const Method = React.lazy(() => import('./pages/marketing/Method').then(module => ({ default: module.Method })));
const Login = React.lazy(() => import('./pages/auth/Login').then(module => ({ default: module.Login })));
const Signup = React.lazy(() => import('./pages/auth/Signup').then(module => ({ default: module.Signup })));
const AuthCallback = React.lazy(() => import('./pages/auth/AuthCallback').then(module => ({ default: module.AuthCallback })));
const CheckEmail = React.lazy(() => import('./pages/auth/CheckEmail').then(module => ({ default: module.CheckEmail })));
const NotFound = React.lazy(() => import('./pages/NotFound').then(module => ({ default: module.NotFound })));

// App pages
const AppLayout = React.lazy(() => import('./components/layout/AppLayout').then(module => ({ default: module.AppLayout })));
const Dashboard = React.lazy(() => import('./pages/Dashboard').then(module => ({ default: module.Dashboard })));
const Journal = React.lazy(() => import('./pages/Journal').then(module => ({ default: module.Journal })));
const Insights = React.lazy(() => import('./pages/Insights').then(module => ({ default: module.Insights })));
const Reset = React.lazy(() => import('./pages/Reset').then(module => ({ default: module.Reset })));
const Library = React.lazy(() => import('./pages/Library').then(module => ({ default: module.Library })));
const MindshiftTracker = React.lazy(() => import('./pages/MindshiftTracker').then(module => ({ default: module.MindshiftTracker })));
const Actions = React.lazy(() => import('./pages/Actions').then(module => ({ default: module.Actions })));
const Resources = React.lazy(() => import('./pages/Resources').then(module => ({ default: module.Resources })));
const Settings = React.lazy(() => import('./pages/Settings').then(module => ({ default: module.Settings })));

// Loading component
const PageLoader: React.FC = () => (
  <div className="min-h-screen bg-mindshift-950 flex items-center justify-center">
    <div className="flex flex-col items-center gap-4">
      <div className="w-8 h-8 border-2 border-gold-400 border-t-transparent rounded-full animate-spin" />
      <p className="text-mindshift-400">Loading...</p>
    </div>
  </div>
);

// Page transition wrapper
const PageTransition: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.2, ease: "easeInOut" }}
    >
      {children}
    </motion.div>
  );
};

// Prefetch helper
const usePrefetch = () => {
  const [prefetched, setPrefetched] = useState(false);
  
  const prefetchAppBundle = () => {
    if (!prefetched) {
      // Trigger prefetch of app components
      import('./pages/Dashboard');
      import('./pages/Journal');
      import('./components/layout/AppLayout');
      setPrefetched(true);
    }
  };
  
  return prefetchAppBundle;
};

function App() {
  const location = useLocation();
  const prefetchAppBundle = usePrefetch();

  // Handle prefetch on "Start free" CTA hover
  React.useEffect(() => {
    const handlePrefetch = () => prefetchAppBundle();
    
    // Add event listeners to start free buttons
    const buttons = document.querySelectorAll('[data-prefetch="app"]');
    buttons.forEach(button => {
      button.addEventListener('mouseenter', handlePrefetch);
    });
    
    return () => {
      buttons.forEach(button => {
        button.removeEventListener('mouseenter', handlePrefetch);
      });
    };
  }, [location.pathname, prefetchAppBundle]);

  return (
    <>
      <UTMPersistence />
      <ScrollToTop />
      <AppReturnBanner />
      <Suspense fallback={<PageLoader />}>
        <AnimatePresence mode="wait">
          <Routes location={location} key={location.pathname}>
            {/* Marketing Routes */}
            <Route path="/" element={
              <PageTransition>
                <LandingPage />
              </PageTransition>
            } />
            <Route path="/pricing" element={
              <PageTransition>
                <Pricing />
              </PageTransition>
            } />
            <Route path="/mentoring" element={
              <PageTransition>
                <Mentoring />
              </PageTransition>
            } />
            <Route path="/webinar" element={
              <PageTransition>
                <Webinar />
              </PageTransition>
            } />
            <Route path="/resources/free-workbook" element={
              <PageTransition>
                <FreeWorkbookLeadMagnet />
              </PageTransition>
            } />
            <Route path="/welcome" element={
              <PageTransition>
                <Welcome />
              </PageTransition>
            } />
            
            {/* Checkout Routes */}
            <Route path="/checkout/success" element={
              <PageTransition>
                <CheckoutSuccess />
              </PageTransition>
            } />
            <Route path="/checkout/cancel" element={
              <PageTransition>
                <CheckoutCancel />
              </PageTransition>
            } />
            
            {/* Legal Routes */}
            <Route path="/privacy" element={
              <PageTransition>
                <Privacy />
              </PageTransition>
            } />
            <Route path="/terms" element={
              <PageTransition>
                <Terms />
              </PageTransition>
            } />
            <Route path="/data-processing" element={
              <PageTransition>
                <DataProcessing />
              </PageTransition>
            } />
            <Route path="/about" element={
              <PageTransition>
                <About />
              </PageTransition>
            } />
            <Route path="/method" element={
              <PageTransition>
                <Method />
              </PageTransition>
            } />
            
            {/* Auth Routes */}
            <Route path="/auth/login" element={
              <PageTransition>
                <Login />
              </PageTransition>
            } />
            <Route path="/auth/signup" element={
              <PageTransition>
                <Signup />
              </PageTransition>
            } />
            <Route path="/auth/callback" element={
              <PageTransition>
                <AuthCallback />
              </PageTransition>
            } />
            <Route path="/auth/check-email" element={
              <PageTransition>
                <CheckEmail />
              </PageTransition>
            } />
            
            {/* App Routes */}
            <Route path="/app" element={
              <PageTransition>
                <JournalContextProvider>
                  <AppLayout />
                </JournalContextProvider>
              </PageTransition>
            }>
              <Route path="dashboard" element={
                <AuthGate>
                  <Dashboard />
                </AuthGate>
              } />
              <Route path="journal" element={
                <GuestAllowed>
                  <Journal />
                </GuestAllowed>
              } />
              <Route path="insights" element={
                <GuestAllowed>
                  <Insights />
                </GuestAllowed>
              } />
              <Route path="reset" element={
                <GuestAllowed>
                  <Reset />
                </GuestAllowed>
              } />
              <Route path="library" element={
                <AuthGate>
                  <Library />
                </AuthGate>
              } />
              <Route path="mindshift" element={
                <AuthGate>
                  <MindshiftTracker />
                </AuthGate>
              } />
              <Route path="actions" element={
                <AuthGate>
                  <Actions />
                </AuthGate>
              } />
              <Route path="settings" element={
                <AuthGate>
                  <Settings />
                </AuthGate>
              } />
              <Route path="resources" element={
                <PlanGate requiredPlan="premium">
                  <Resources />
                </PlanGate>
              } />
              {/* Default app route redirects to journal (guest-friendly) */}
              <Route path="" element={<Navigate to="journal" replace />} />
            </Route>
            
            {/* Catch-all route */}
            <Route path="*" element={
              <PageTransition>
                <NotFound />
              </PageTransition>
            } />
          </Routes>
        </AnimatePresence>
      </Suspense>
    </>
  );
}

export default App;
